import React, { useState, useEffect, useMemo } from 'react';
import { 
  Send, Bell, Mail, Smartphone, CheckCircle, XCircle, 
  AlertCircle, Loader, RefreshCw, Database, Server, Wifi, Users,
  Search, X
} from 'lucide-react';
import { auth } from '../lib/firebase';
import { supabase } from '../lib/supabaseClient';
import { notificationService, NotificationResponse } from '../services/notificationService';
import './TestNotification.css';

interface TestResult {
  step: string;
  status: 'pending' | 'success' | 'error' | 'warning';
  message: string;
  timestamp: string;
  details?: any;
}

// ============ NEW TAB TYPES ============
type TabKey = 'manual' | 'broadcast' | 'targetUser' | 'targetShop' | 'admins';

interface UserRow {
  id: string;
  firebase_uid: string;
  name: string;
  email: string | null;
  created_at: string;
}

interface ShopRow {
  id: string;
  user_id: string;
  shop_name: string;
  business_email: string | null;
  created_at: string;
  location?: string | null;
  is_active?: boolean | null;
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

// ============ EXTRA SHOP FILTERS (NEW) ============
type ShopActivityFilter = 'all' | 'active' | 'inactive' | 'recent';
type ShopRangeFilter = 'all' | '7d' | '30d' | '90d';

const TestNotification: React.FC = () => {
  // ============ NEW TAB STATE ============
  const [activeTab, setActiveTab] = useState<TabKey>('manual');

  // Broadcast state
  const [allUsers, setAllUsers] = useState<UserRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [selectedBatchIndex, setSelectedBatchIndex] = useState<number | null>(null);

  // Target user state
  const [targetUsers, setTargetUsers] = useState<UserRow[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [userSearch, setUserSearch] = useState('');
  const [userLetter, setUserLetter] = useState<string>('');

  // Target shop state
  const [targetShops, setTargetShops] = useState<ShopRow[]>([]);
  const [selectedShopIds, setSelectedShopIds] = useState<Set<string>>(new Set());
  const [shopSearch, setShopSearch] = useState('');
  const [shopLetter, setShopLetter] = useState<string>('');
  // ============ EXTRA SHOP FILTERS (NEW) ============
  const [shopActivity, setShopActivity] = useState<ShopActivityFilter>('all');
  const [shopRange, setShopRange] = useState<ShopRangeFilter>('all');
  const [shopLocationKeyword, setShopLocationKeyword] = useState('');

  // ============ EXISTING STATE (UNCHANGED) ============
  const [title, setTitle] = useState('Production Test');
  const [body, setBody] = useState('This is a production test notification');
  
  // Support for multiple emails
  const [emailInput, setEmailInput] = useState('gostoredevelopment@gmail.com, builders785@gmail.com');
  const [emailList, setEmailList] = useState<string[]>(['gostoredevelopment@gmail.com', 'builders785@gmail.com']);
  
  // Support for multiple user IDs
  const [userIdInput, setUserIdInput] = useState('uKCPqGH4TwNICOdptJxsY487kJm2, s7OG1hobkyY1UAAkk2uPPH5P6dP2');
  const [userIdList, setUserIdList] = useState<string[]>(['uKCPqGH4TwNICOdptJxsY487kJm2', 's7OG1hobkyY1UAAkk2uPPH5P6dP2']);
  
  const [notificationType, setNotificationType] = useState<'order' | 'chat' | 'system' | 'vendor'>('system');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [results, setResults] = useState<TestResult[]>([]);
  const [latestResponse, setLatestResponse] = useState<NotificationResponse | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [isAuthReady, setIsAuthReady] = useState(false);

  // Initialize auth
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setCurrentUserId(user.uid);
        setIsAuthReady(true);
        addResult('AUTH', 'success', `Logged in: ${user.uid.substring(0, 12)}...`);
        
        // Set default user ID to current user
        if (userIdList.length === 1 && userIdList[0] === 'uKCPqGH4TwNICOdptJxsY487kJm2') {
          setUserIdList([user.uid]);
          setUserIdInput(user.uid);
        }
      } else {
        setCurrentUserId('');
        setIsAuthReady(false);
        addResult('AUTH', 'error', 'Not logged in');
      }
    });

    return () => unsubscribe();
  }, []);

  // ============ FETCH USERS FOR BROADCAST (NEW) ============
  useEffect(() => {
    if (activeTab === 'broadcast' && allUsers.length === 0 && !usersLoading) {
      fetchAllUsers();
    }
  }, [activeTab]);

  // ============ FETCH USERS FOR TARGET USER TAB (NEW) ============
  useEffect(() => {
    if (activeTab === 'targetUser' && targetUsers.length === 0 && !usersLoading) {
      fetchTargetUsers();
    }
  }, [activeTab]);

  // ============ FETCH SHOPS FOR TARGET SHOP TAB (NEW) ============
  useEffect(() => {
    if (activeTab === 'targetShop' && targetShops.length === 0) {
      fetchTargetShops();
    }
  }, [activeTab]);

  const fetchAllUsers = async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, firebase_uid, name, email, created_at')
        .not('firebase_uid', 'is', null)
        .order('created_at', { ascending: true });
      if (error) throw error;
      setAllUsers((data || []) as UserRow[]);
    } catch (err: any) {
      setUsersError(err?.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchTargetUsers = async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, firebase_uid, name, email, created_at')
        .not('firebase_uid', 'is', null)
        .order('created_at', { ascending: true });
      if (error) throw error;
      setTargetUsers((data || []) as UserRow[]);
    } catch (err: any) {
      setUsersError(err?.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  // ============ FETCH SHOPS — now pulls extra cols for filters (NEW) ============
  const fetchTargetShops = async () => {
    try {
      const { data, error } = await supabase
        .from('vendor_profiles')
        .select('id, user_id, shop_name, business_email, created_at, location, is_active')
        .order('created_at', { ascending: true });
      if (error) throw error;
      setTargetShops((data || []) as ShopRow[]);
    } catch (err: any) {
      setUsersError(err?.message || 'Failed to load shops');
    }
  };

  // ============ BATCH HELPERS (NEW) ============
  const batches: UserRow[][] = [];
  for (let i = 0; i < allUsers.length; i += 15) {
    batches.push(allUsers.slice(i, i + 15));
  }

  // ============ EXISTING METHODS (UNCHANGED) ============
  const addResult = (step: string, status: TestResult['status'], message: string, details?: any) => {
    const result: TestResult = {
      step,
      status,
      message,
      timestamp: new Date().toLocaleTimeString(),
      details
    };
    
    console.log(`${getStatusEmoji(status)} [${result.timestamp}] ${step}: ${message}`, details || '');
    setResults(prev => [result, ...prev.slice(0, 20)]);
  };

  const getStatusEmoji = (status: TestResult['status']) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'pending': return '⏳';
      default: return 'ℹ️';
    }
  };

  const parseCommaSeparated = (input: string): string[] => {
    return input.split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0);
  };

  const handleEmailChange = (value: string) => {
    setEmailInput(value);
    setEmailList(parseCommaSeparated(value));
  };

  const handleUserIdChange = (value: string) => {
    setUserIdInput(value);
    setUserIdList(parseCommaSeparated(value));
  };

  // ============ FILTERED LISTS (NEW) ============
  const filteredTargetUsers = useMemo(() => {
    let list = targetUsers;
    if (userLetter) {
      list = list.filter(u => (u.name || '').toUpperCase().startsWith(userLetter));
    }
    if (userSearch.trim()) {
      const q = userSearch.toLowerCase();
      list = list.filter(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [targetUsers, userLetter, userSearch]);

  // ============ EXTENDED SHOP FILTERING (NEW) ============
  const filteredTargetShops = useMemo(() => {
    let list = targetShops;
    if (shopLetter) {
      list = list.filter(s => (s.shop_name || '').toUpperCase().startsWith(shopLetter));
    }
    if (shopSearch.trim()) {
      const q = shopSearch.toLowerCase();
      list = list.filter(s =>
        (s.shop_name || '').toLowerCase().includes(q) ||
        (s.business_email || '').toLowerCase().includes(q)
      );
    }
    if (shopLocationKeyword.trim()) {
      const q = shopLocationKeyword.toLowerCase();
      list = list.filter(s => (s.location || '').toLowerCase().includes(q));
    }
    if (shopActivity === 'active') {
      list = list.filter(s => s.is_active === true);
    } else if (shopActivity === 'inactive') {
      list = list.filter(s => s.is_active === false);
    }
    if (shopRange !== 'all') {
      const now = Date.now();
      const windowMs =
        shopRange === '7d' ? 7 * 86400000 :
        shopRange === '30d' ? 30 * 86400000 :
        90 * 86400000;
      list = list.filter(s => {
        const t = new Date(s.created_at).getTime();
        return !isNaN(t) && now - t <= windowMs;
      });
    }
    return list;
  }, [targetShops, shopLetter, shopSearch, shopLocationKeyword, shopActivity, shopRange]);

  // ============ TAB-BASED TARGET COLLECTION (NEW) ============
  // UPDATED: every tab now returns BOTH userIds and emails when available.
  const collectTargets = (): { userIds: string[]; emails: string[] } => {
    if (activeTab === 'manual') {
      return { userIds: userIdList, emails: emailList };
    }
    if (activeTab === 'broadcast') {
      if (selectedBatchIndex === null || !batches[selectedBatchIndex]) {
        return { userIds: [], emails: [] };
      }
      const batch = batches[selectedBatchIndex];
      return {
        userIds: batch.map(u => u.firebase_uid).filter((x): x is string => !!x),
        emails: batch.map(u => u.email).filter((e): e is string => !!e)
      };
    }
    if (activeTab === 'targetUser') {
      const picked = targetUsers.filter(u => selectedUserIds.has(u.firebase_uid));
      return {
        userIds: picked.map(u => u.firebase_uid).filter((x): x is string => !!x),
        emails: picked.map(u => u.email).filter((e): e is string => !!e)
      };
    }
    if (activeTab === 'targetShop') {
      // UPDATED: include the shop's user_id (for push) + business_email (for email) when present.
      const picked = targetShops.filter(s => selectedShopIds.has(s.id));
      const emails = picked
        .map(s => s.business_email)
        .filter((e): e is string => !!e);
      const userIds = picked
        .map(s => s.user_id)
        .filter((x): x is string => !!x);
      return { userIds, emails };
    }
    if (activeTab === 'admins') {
      return { userIds: userIdList, emails: emailList };
    }
    return { userIds: [], emails: [] };
  };

  const sendNotification = async () => {
    if (!isAuthReady) {
      addResult('VALIDATION', 'error', 'Please log in first');
      return;
    }

    const { userIds, emails } = collectTargets();

    if (emails.length === 0 && userIds.length === 0) {
      addResult('VALIDATION', 'error', 'At least one email or user ID required');
      return;
    }

    setIsSending(true);
    setResults([]);
    
    const requestId = `test_${Date.now()}`;
    
    addResult('START', 'pending', '🚀 Starting notification test');
    addResult('TARGETS', 'success', `Targeting: ${userIds.length} users, ${emails.length} emails`);

    // Prepare notification data
    const notificationData: any = {
      title,
      body,
      notification_type: notificationType,
      redirect_url: redirectUrl || undefined,
      data: {
        test: true,
        timestamp: new Date().toISOString(),
        source: 'TestNotification',
        requestId
      }
    };

    // Add target users - service will handle both formats
    if (userIds.length > 0) {
      if (userIds.length === 1) {
        Object.assign(notificationData, { target_user_id: userIds[0] });
      } else {
        Object.assign(notificationData, { target_user_ids: userIds });
      }
    }

    // Add emails - service will handle both formats
    if (emails.length > 0) {
      if (emails.length === 1) {
        Object.assign(notificationData, { email: emails[0] });
      } else {
        Object.assign(notificationData, { email_list: emails });
      }
    }

    addResult('SENDING', 'pending', '📤 Sending to NotificationService...');

    try {
      // Call the service - it handles all backend communication
      const response = await notificationService.sendNotification(notificationData);
      
      setLatestResponse(response);
      
      if (response.success) {
        addResult('SUCCESS', 'success', 
          `✅ Success via ${response.channels.join(', ')}`, 
          { 
            channels: response.channels,
            users: response.targetUsers,
            emails: response.emails,
            results: response.results
          }
        );
      } else {
        addResult('FAILED', 'error', `❌ Failed: ${response.error || response.message}`);
      }
      
    } catch (error: any) {
      addResult('ERROR', 'error', `❌ Error: ${error.message}`);
      console.error('❌ Service error:', error);
      
    } finally {
      setIsSending(false);
    }
  };

  const clearResults = () => {
    setResults([]);
    setLatestResponse(null);
  };

  const getSuccessCount = () => results.filter(r => r.status === 'success').length;
  const getErrorCount = () => results.filter(r => r.status === 'error').length;

  // ============ SEARCH + ALPHABET FILTER COMPONENT (NEW) ============
  const FilterBar: React.FC<{
    search: string;
    onSearch: (v: string) => void;
    letter: string;
    onLetter: (v: string) => void;
    placeholder: string;
  }> = ({ search, onSearch, letter, onLetter, placeholder }) => (
    <div className="tn-filter">
      <div className="tn-search">
        <Search size={14} className="tn-search-icon" />
        <input
          type="text"
          value={search}
          onChange={e => onSearch(e.target.value)}
          placeholder={placeholder}
          className="tn-search-input"
        />
        {search && (
          <button className="tn-search-clear" onClick={() => onSearch('')}>
            <X size={12} />
          </button>
        )}
      </div>
      <div className="tn-alpha">
        <button
          className={`tn-alpha-btn ${letter === '' ? 'active' : ''}`}
          onClick={() => onLetter('')}
        >
          All
        </button>
        {ALPHABET.map(l => (
          <button
            key={l}
            className={`tn-alpha-btn ${letter === l ? 'active' : ''}`}
            onClick={() => onLetter(l)}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );

  // ============ SHOP EXTRA FILTERS COMPONENT (NEW) ============
  const ShopExtraFilters: React.FC = () => (
    <div className="tn-filter">
      <div className="tn-search">
        <Search size={14} className="tn-search-icon" />
        <input
          type="text"
          value={shopLocationKeyword}
          onChange={e => setShopLocationKeyword(e.target.value)}
          placeholder="Filter by location keyword (e.g. Lagos, Abuja)"
          className="tn-search-input"
        />
        {shopLocationKeyword && (
          <button className="tn-search-clear" onClick={() => setShopLocationKeyword('')}>
            <X size={12} />
          </button>
        )}
      </div>

      <div className="tn-alpha" style={{ marginBottom: 6 }}>
        <button
          className={`tn-alpha-btn ${shopActivity === 'all' ? 'active' : ''}`}
          onClick={() => setShopActivity('all')}
        >
          All
        </button>
        <button
          className={`tn-alpha-btn ${shopActivity === 'active' ? 'active' : ''}`}
          onClick={() => setShopActivity('active')}
        >
          Active
        </button>
        <button
          className={`tn-alpha-btn ${shopActivity === 'inactive' ? 'active' : ''}`}
          onClick={() => setShopActivity('inactive')}
        >
          Inactive
        </button>
      </div>

      <div className="tn-alpha">
        <button
          className={`tn-alpha-btn ${shopRange === 'all' ? 'active' : ''}`}
          onClick={() => setShopRange('all')}
        >
          Any
        </button>
        <button
          className={`tn-alpha-btn ${shopRange === '7d' ? 'active' : ''}`}
          onClick={() => setShopRange('7d')}
        >
          Last 7d
        </button>
        <button
          className={`tn-alpha-btn ${shopRange === '30d' ? 'active' : ''}`}
          onClick={() => setShopRange('30d')}
        >
          Last 30d
        </button>
        <button
          className={`tn-alpha-btn ${shopRange === '90d' ? 'active' : ''}`}
          onClick={() => setShopRange('90d')}
        >
          Last 90d
        </button>
      </div>
    </div>
  );

  // ============ RENDER HELPERS FOR TABS (NEW) ============
  const renderBroadcastTab = () => {
    const selectedBatch = selectedBatchIndex !== null ? batches[selectedBatchIndex] : null;
    return (
      <div>
        <p className="tn-hint">
          Each badge = 15 users, oldest first. Total: {allUsers.length} users → {batches.length} batches.
        </p>
        {usersLoading && <div className="tn-center"><Loader className="spin" size={20} /></div>}
        {usersError && <div className="tn-error">{usersError}</div>}
        {!usersLoading && !usersError && batches.length === 0 && (
          <p className="tn-empty-text">No users available.</p>
        )}
        {batches.length > 0 && (
          <div className="tn-badge-grid">
            {batches.map((batch, idx) => {
              const start = idx * 15 + 1;
              const end = idx * 15 + batch.length;
              return (
                <div
                  key={idx}
                  className={`tn-badge ${idx === selectedBatchIndex ? 'selected' : ''}`}
                  onClick={() => setSelectedBatchIndex(idx)}
                >
                  <div className="tn-badge-num">#{idx + 1}</div>
                  <div className="tn-badge-sub">{start}–{end}</div>
                  <div className="tn-badge-sub">{batch.length} users</div>
                </div>
              );
            })}
          </div>
        )}

        {selectedBatch && (
          <div className="tn-batch-details">
            <div className="tn-batch-header">
              <span className="tn-batch-title">
                Batch #{selectedBatchIndex! + 1} — {selectedBatch.length} users
              </span>
            </div>
            <div className="tn-list">
              {selectedBatch.map(u => (
                <div key={u.id} className="tn-list-row static">
                  <div className="tn-row-info">
                    <div className="tn-row-name">{u.name || '(no name)'}</div>
                    <div className="tn-row-email">{u.email || '(no email)'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTargetUserTab = () => (
    <div>
      <FilterBar
        search={userSearch}
        onSearch={setUserSearch}
        letter={userLetter}
        onLetter={setUserLetter}
        placeholder="Search users by name or email..."
      />
      {usersLoading && <div className="tn-center"><Loader className="spin" size={20} /></div>}
      {usersError && <div className="tn-error">{usersError}</div>}
      {!usersLoading && !usersError && (
        <div className="tn-list">
          {filteredTargetUsers.length === 0 && (
            <div className="tn-empty-text" style={{ padding: 20, textAlign: 'center' }}>
              No users match.
            </div>
          )}
          {filteredTargetUsers.map(u => {
            const checked = selectedUserIds.has(u.firebase_uid);
            return (
              <div
                key={u.id}
                className={`tn-list-row ${checked ? 'checked' : ''}`}
                onClick={() => {
                  const next = new Set(selectedUserIds);
                  if (checked) {
                    next.delete(u.firebase_uid);
                  } else {
                    if (next.size >= 15) return;
                    next.add(u.firebase_uid);
                  }
                  setSelectedUserIds(next);
                }}
              >
                <div className="tn-row-info">
                  <div className="tn-row-name">{u.name || '(no name)'}</div>
                  <div className="tn-row-email">{u.email || '(no email)'}</div>
                </div>
                <input
                  type="checkbox"
                  checked={checked}
                  readOnly
                  className="tn-checkbox"
                />
              </div>
            );
          })}
        </div>
      )}
      <div className="tn-counter">{selectedUserIds.size} / 15 selected</div>
    </div>
  );

  const renderTargetShopTab = () => (
    <div>
      <FilterBar
        search={shopSearch}
        onSearch={setShopSearch}
        letter={shopLetter}
        onLetter={setShopLetter}
        placeholder="Search shops by name or email..."
      />
      <ShopExtraFilters />
      {targetShops.length === 0 && <div className="tn-center"><Loader className="spin" size={20} /></div>}
      <div className="tn-list">
        {filteredTargetShops.length === 0 && targetShops.length > 0 && (
          <div className="tn-empty-text" style={{ padding: 20, textAlign: 'center' }}>
            No shops match.
          </div>
        )}
        {filteredTargetShops.map(sh => {
          const checked = selectedShopIds.has(sh.id);
          return (
            <div
              key={sh.id}
              className={`tn-list-row ${checked ? 'checked' : ''}`}
              onClick={() => {
                const next = new Set(selectedShopIds);
                if (checked) {
                  next.delete(sh.id);
                } else {
                  if (next.size >= 15) return;
                  next.add(sh.id);
                }
                setSelectedShopIds(next);
              }}
            >
              <div className="tn-row-info">
                <div className="tn-row-name">{sh.shop_name}</div>
                <div className="tn-row-email">{sh.business_email || '(no email)'}</div>
                {sh.location && (
                  <div className="tn-row-email" style={{ marginTop: 2 }}>
                    📍 {sh.location}
                  </div>
                )}
              </div>
              <input
                type="checkbox"
                checked={checked}
                readOnly
                className="tn-checkbox"
              />
            </div>
          );
        })}
      </div>
      <div className="tn-counter">{selectedShopIds.size} / 15 selected</div>
    </div>
  );

  return (
    <div className="test-notification-container">
      <div className="test-header">
        <h1><Bell size={24} /> Notification System Test</h1>
        <p className="subtitle">✅ SIMPLE SERVICE PATTERN - Direct backend calls via service.</p>
      </div>

      {/* ============ TABS (NEW) ============ */}
      <div className="tn-tabs-bar">
        <button className={`tn-tab ${activeTab === 'manual' ? 'active' : ''}`} onClick={() => setActiveTab('manual')}>Manual</button>
        <button className={`tn-tab ${activeTab === 'broadcast' ? 'active' : ''}`} onClick={() => setActiveTab('broadcast')}>Broadcast</button>
        <button className={`tn-tab ${activeTab === 'targetUser' ? 'active' : ''}`} onClick={() => setActiveTab('targetUser')}>Target User</button>
        <button className={`tn-tab ${activeTab === 'targetShop' ? 'active' : ''}`} onClick={() => setActiveTab('targetShop')}>Target Shop</button>
        <button className={`tn-tab ${activeTab === 'admins' ? 'active' : ''}`} onClick={() => setActiveTab('admins')}>Admins</button>
      </div>

      <div className="test-grid">
        {/* Left Column: Configuration */}
        <div className="config-section">
          <h2><Send size={20} /> Test Configuration</h2>
          
          <div className="form-group">
            <label>Notification Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter notification title"
            />
          </div>

          <div className="form-group">
            <label>Notification Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Enter notification message"
              rows={3}
            />
          </div>

          {/* ============ TAB CONTENT (NEW) ============ */}
          {activeTab === 'manual' && (
            <>
              <div className="form-group">
                <label>
                  <Mail size={14} /> Recipient Emails (comma separated)
                </label>
                <input
                  type="text"
                  value={emailInput}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  placeholder="email1@example.com, email2@example.com"
                  className="mono-input"
                />
                <small>{emailList.length} email(s) - {emailList.length > 1 ? 'Batch send' : 'Single email'}</small>
              </div>

              <div className="form-group">
                <label>
                  <Users size={14} /> Target User IDs (comma separated)
                </label>
                <input
                  type="text"
                  value={userIdInput}
                  onChange={(e) => handleUserIdChange(e.target.value)}
                  placeholder="user_id_1, user_id_2"
                  className="mono-input"
                />
                <small>{userIdList.length} user(s) - {userIdList.length > 1 ? 'Individual sends' : 'Single user'}</small>
              </div>
            </>
          )}

          {activeTab === 'broadcast' && renderBroadcastTab()}
          {activeTab === 'targetUser' && renderTargetUserTab()}
          {activeTab === 'targetShop' && renderTargetShopTab()}
          {activeTab === 'admins' && (
            <>
              <div className="form-group">
                <label>
                  <Mail size={14} /> Admin Emails (comma separated)
                </label>
                <input
                  type="text"
                  value={emailInput}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  placeholder="admin1@example.com, admin2@example.com"
                  className="mono-input"
                />
                <small>{emailList.length} admin email(s)</small>
              </div>

              <div className="form-group">
                <label>
                  <Users size={14} /> Admin User IDs (comma separated)
                </label>
                <input
                  type="text"
                  value={userIdInput}
                  onChange={(e) => handleUserIdChange(e.target.value)}
                  placeholder="admin_user_id_1, admin_user_id_2"
                  className="mono-input"
                />
                <small>{userIdList.length} admin user(s)</small>
              </div>
            </>
          )}

          <div className="form-group">
            <label>Notification Type</label>
            <select 
              value={notificationType} 
              onChange={(e) => setNotificationType(e.target.value as any)}
            >
              <option value="system">System</option>
              <option value="order">Order</option>
              <option value="chat">Chat</option>
              <option value="vendor">Vendor</option>
            </select>
          </div>

          <div className="form-group">
            <label>Redirect URL (Optional)</label>
            <input
              type="url"
              value={redirectUrl}
              onChange={(e) => setRedirectUrl(e.target.value)}
              placeholder="https://example.com"
            />
          </div>

          <div className="form-group">
            <label>Current User ID</label>
            <input
              type="text"
              value={currentUserId ? `${currentUserId.substring(0, 16)}...` : 'Not logged in'}
              readOnly
              disabled
              className="mono-input"
            />
            <small>Will be used as default target</small>
          </div>

          <div className="action-buttons">
            <button 
              onClick={sendNotification} 
              disabled={isSending || !isAuthReady}
              className="send-btn"
            >
              {isSending ? (
                <><Loader size={16} className="spin" /> Sending...</>
              ) : (
                <><Send size={16} /> Send Notification</>
              )}
            </button>
            
            <button onClick={clearResults} className="clear-btn" disabled={isSending}>
              <RefreshCw size={16} /> Clear
            </button>
          </div>

          {!isAuthReady && (
            <div className="auth-warning">
              <AlertCircle size={16} />
              <p>Please log in to send notifications</p>
            </div>
          )}
        </div>

        {/* Right Column: Results & Logs */}
        <div className="results-section">
          <div className="results-header">
            <h2><Database size={20} /> Results</h2>
            <div className="stats">
              <span className="stat success"><CheckCircle size={14} /> {getSuccessCount()}</span>
              <span className="stat error"><XCircle size={14} /> {getErrorCount()}</span>
              <span className="stat total"><Bell size={14} /> {results.length}</span>
            </div>
          </div>

          {latestResponse && (
            <div className={`response-summary ${latestResponse.success ? 'success' : 'error'}`}>
              <div className="summary-header">
                <h3>{latestResponse.success ? '✅ Success' : '❌ Failed'}</h3>
                <span className="timestamp">{new Date(latestResponse.timestamp).toLocaleTimeString()}</span>
              </div>
              
              <p>{latestResponse.message}</p>
              
              {latestResponse.success && (
                <div className="channels">
                  <strong>Channels:</strong>
                  <div className="channel-tags">
                    {latestResponse.channels?.map((channel, idx) => (
                      <span key={idx} className={`channel-tag ${channel}`}>
                        {channel === 'email' && <Mail size={12} />}
                        {channel === 'onesignal' && <Smartphone size={12} />}
                        {channel}
                      </span>
                    ))}
                  </div>
                  <div className="recipient-stats">
                    <span>👥 Users: {latestResponse.targetUsers || 0}</span>
                    <span>📧 Emails: {latestResponse.emails || 0}</span>
                  </div>
                  
                  {latestResponse.results && (
                    <div className="detailed-results">
                      <strong>Details:</strong>
                      {latestResponse.results.push && (
                        <div className={`push-result ${latestResponse.results.push.success ? 'success' : 'error'}`}>
                          Push: {latestResponse.results.push.success 
                            ? `✅ ${latestResponse.results.push.count} sent` 
                            : `❌ ${latestResponse.results.push.error || 'Failed'}`}
                        </div>
                      )}
                      {latestResponse.results.email && (
                        <div className={`email-result ${latestResponse.results.email.success ? 'success' : 'error'}`}>
                          Email: {latestResponse.results.email.success 
                            ? `✅ ${latestResponse.results.email.count} sent` 
                            : `❌ ${latestResponse.results.email.error || 'Failed'}`}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="results-list">
            <h3>Step-by-Step Log</h3>
            {results.length === 0 ? (
              <div className="empty-results">
                <Bell size={32} />
                <p>No test results yet. Click "Send Notification" to start.</p>
              </div>
            ) : (
              <div className="results-scroll">
                {results.map((result, index) => (
                  <div key={index} className={`result-item ${result.status}`}>
                    <div className="result-header">
                      <span className="result-status">{getStatusEmoji(result.status)}</span>
                      <span className="result-step">{result.step}</span>
                      <span className="result-time">{result.timestamp}</span>
                    </div>
                    <div className="result-message">{result.message}</div>
                    {result.details && (
                      <button 
                        className="view-details-btn"
                        onClick={() => console.log('Details:', result.details)}
                      >
                        View Details
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="debug-info">
        <h3><Server size={16} /> System Status</h3>
        <div className="info-grid">
          <div className="info-item">
            <strong>Architecture:</strong> Service → Backend API
          </div>
          <div className="info-item">
            <strong>Auth:</strong> {isAuthReady ? '✅' : '❌'}
          </div>
          <div className="info-item">
            <strong>Backend URL:</strong> http://localhost:3000
          </div>
          <div className="info-item">
            <strong>Push API:</strong> /api/notifications/send
          </div>
          <div className="info-item">
            <strong>Email API:</strong> /api/notifications/create
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestNotification;